/**
 * Where WT-5a invoices show up outside their own pages:
 *
 *   WtTicketInvoiceLink   on a customer-approved field ticket — "Not invoiced
 *                         yet" with a link to New invoice, or the invoice
 *                         that holds it. The ticket payload has NO invoice
 *                         field, so the holder is DERIVED (wtHoldingInvoice)
 *                         from the customer's invoices, and the page says so.
 *   WtJobInvoicesSection  on a job's Tickets tab — the invoices whose job is
 *                         this one. An invoice billing tickets from several
 *                         jobs carries no job, so it is not listed here, and
 *                         the panel says that too.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { useWtBillable, useWtInvoiceDetails, useWtInvoices } from '../../../hooks/useWtInvoices';
import { fmtCurrency } from '../../../lib/display-labels';
import { queryLoadFailure } from '../../../lib/load-failure';
import { wtDay } from '../../../lib/well-testing';
import { wtHoldingInvoice, wtInvoiceStatusSkin } from '../../../lib/wt-invoices';
import type { WtInvoiceRow } from '../../../types/wt-invoices';
import { useOpenRecord } from '../../downhole/recordPage';
import { WtBadge, WtEmpty, WtPanel, WtRegister, type WtColumn } from '../WtBits';
import { WT_NEW_INVOICE_PATH } from './InvoicesScreen';

export function WtTicketInvoiceLink({ ticketId, customerId }: { ticketId: string; customerId: string | null }) {
  const billable = useWtBillable();
  const groups = billable.data?.data ?? [];
  const ready = groups.some((g) => g.tickets.some((t) => t.ticketId === ticketId));
  const billableKnown = !billable.isLoading && !queryLoadFailure(billable);
  const list = useWtInvoices(customerId ? { customer_id: customerId } : undefined, billableKnown && !ready && !!customerId);
  const candidates = (list.data?.data ?? []).filter((r) => r.status !== 'void').map((r) => r.id);
  const details = useWtInvoiceDetails(candidates, billableKnown && !ready);
  const holder = wtHoldingInvoice(details.map((d) => d.data?.data ?? null), ticketId);
  const loading = billable.isLoading || list.isLoading || details.some((d) => d.isLoading);
  const failed = !!queryLoadFailure(billable) || !!queryLoadFailure(list) || details.some((d) => !!queryLoadFailure(d));

  let body: React.ReactNode;
  if (billable.isLoading) body = 'Checking whether this ticket is invoiced…';
  else if (ready) {
    body = (
      <>
        Not invoiced yet.{' '}
        <Link className="font-bold underline" to={`${WT_NEW_INVOICE_PATH}?ticket=${ticketId}`} data-testid="wt-ticket-invoice-ready">
          Put it on a new invoice
        </Link>
        .
      </>
    );
  } else if (holder) {
    body = (
      <>
        On invoice{' '}
        <Link className="font-mono font-bold underline" to={`/well-testing/invoices/${holder.id}`} data-testid="wt-ticket-invoice-holder">
          {holder.invoiceNumber}
        </Link>{' '}
        ({holder.statusLabel || holder.status}).
      </>
    );
  } else if (loading) body = 'Checking whether this ticket is invoiced…';
  else if (failed) body = 'Whether this ticket is invoiced could not be read.';
  else body = 'This ticket is neither waiting to be invoiced nor on a live invoice.';

  return (
    <div role="note" data-testid="wt-ticket-invoice" className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text">
      <p>{body}</p>
      <p className="mt-1 text-xs text-fieldiq-muted">Worked out from the customer's invoices: the ticket itself does not record its invoice.</p>
    </div>
  );
}

const jobInvoiceColumns: WtColumn<WtInvoiceRow>[] = [
  { key: 'number', header: 'Invoice', render: (r) => <span className="font-mono font-extrabold text-white">{r.invoiceNumber || '—'}</span> },
  { key: 'status', header: 'Status', render: (r) => <WtBadge skin={wtInvoiceStatusSkin(r.status)} value={r.status} /> },
  { key: 'date', header: 'Invoice date', render: (r) => wtDay(r.invoiceDate) },
  { key: 'due', header: 'Due', render: (r) => wtDay(r.dueDate) },
  { key: 'total', header: 'Total', align: 'right', render: (r) => <span className="font-mono">{fmtCurrency(r.total, r.currency)}</span> },
];

export function WtJobInvoicesSection({ jobId }: { jobId: string }) {
  const openRecord = useOpenRecord();
  const query = useWtInvoices({ job_id: jobId });
  const rows = query.data?.data ?? [];
  return (
    <WtPanel
      eyebrow="Billed"
      title="Invoices"
      testId="wt-job-invoices"
      footer="An invoice billing tickets from several jobs is not listed here: find it on the Invoices register."
    >
      <WtRegister
        query={query}
        rows={rows}
        columns={jobInvoiceColumns}
        rail={(r) => wtInvoiceStatusSkin(r.status).rail}
        onOpen={(r) => openRecord(`/well-testing/invoices/${r.id}`)}
        subject="this job's invoices"
        testId="wt-job-invoice"
        minWidth="min-w-[640px]"
        empty={<WtEmpty>No invoice bills this job yet.</WtEmpty>}
        card={(r) => (
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate font-mono text-sm font-extrabold text-white">{r.invoiceNumber || '—'}</p>
              <p className="mt-0.5 text-xs text-fieldiq-muted">Dated {wtDay(r.invoiceDate)} · {fmtCurrency(r.total, r.currency)}</p>
            </div>
            <WtBadge skin={wtInvoiceStatusSkin(r.status)} value={r.status} />
          </div>
        )}
      />
    </WtPanel>
  );
}
